import React, { useEffect, useState } from 'react';
import useWebRTC from '../hooks/useWebRTC';
import useContainerSize from '../hooks/useContainerSize';
import useResponsiveGrid from '../hooks/useResponsiveGrid';
import VideoPlayer from './VideoPlayer';
import Chat from './Chat';
import Controls from './Controls';
import Participants from './Participants';
import SettingsModal from './SettingsModal';
import ChatPopout from './ChatPopout';
import LeaveConfirmationModal from './LeaveConfirmationModal';
import '../styles/Room.css';

const Room = ({
    roomId,
    nickname,
    setJoined,
    localStream,
    setLocalStream,
    audioEnabled,
    setAudioEnabled,
    videoEnabled,
    setVideoEnabled,
}) => {
    const {
        remoteUsers,
        pinnedUserId,
        screenSharingUserId,
        chatMessages,
        isScreenSharing,
        isScreenShareActive,
        sharedStream,
        thumbnailParticipants,
        handleScreenShare,
        handlePinUser,
        sendChatMessage,
        leaveRoom,
        localScreenStream,
        socketRef,
        // eslint-disable-next-line no-unused-vars
        startRecording,
        // eslint-disable-next-line no-unused-vars
        stopRecording,
        isRecording
    } = useWebRTC(roomId, nickname, localStream, { audioEnabled, videoEnabled });

    const [message, setMessage] = useState('');
    const [showParticipants, setShowParticipants] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [showChatPopout, setShowChatPopout] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [raisedHands, setRaisedHands] = useState({});
    const [viewMode, setViewMode] = useState('gallery'); // 'gallery', 'speaker', 'focus', 'immersive'
    const [meetingStartTime] = useState(Date.now());
    const [showViewOptions, setShowViewOptions] = useState(false);
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [controlsVisible, setControlsVisible] = useState(true);
    const [recordingMessage, setRecordingMessage] = useState(null);

    // Use new hooks for responsive layout
    const containerSize = useContainerSize();
    const participantCount = remoteUsers.length + 1;
    const gridConfig = useResponsiveGrid(participantCount, showChat, containerSize.width);

    const handleCameraToggle = () => {
        const newState = !videoEnabled;
        setVideoEnabled(newState);
        if (localStream) {
            localStream.getVideoTracks()[0].enabled = newState;
        }
    };


    useEffect(() => {
        if (!socketRef.current) return;

        const handleRecordingStatus = (status) => {
            if (status === 'started') {
                setRecordingMessage('🔴 Recording started');
            } else if (status === 'stopped') {
                setRecordingMessage('⏹️ Recording stopped, Your recorded video will start downloading shortly..');

                // auto-hide after 4 seconds
                setTimeout(() => setRecordingMessage(null), 4000);
            }
        };

        socketRef.current.on('recording-status', handleRecordingStatus);
        socketRef.current.on('recording-browser-crashed', ()=> {
              setRecordingMessage('Recording stopped');
        })

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            socketRef.current.off('recording-status', handleRecordingStatus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for raise-hand events
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket) return;

        const handleRaiseHand = (userId, isRaised, userNickname) => {
            setRaisedHands(prev => ({
                ...prev,
                [userId]: isRaised
            }));

            if (isRaised && userId !== socket.id) {
                showToast(`${userNickname} raised their hand ✋`);
            }
        };

        socket.on('raise-hand-broadcast', handleRaiseHand);
        return () => {
            socket.off('raise-hand-broadcast', handleRaiseHand);
        };
    }, [socketRef]);

    const toggleParticipants = () => {
        setShowParticipants(!showParticipants);
        if (showChat) setShowChat(false);
    };

    const toggleChat = () => {
        setShowChat(!showChat);
        if (showParticipants) setShowParticipants(false);
    };

    // Keyboard shortcuts & Global Fraud Prevention
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore if typing in input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key.toLowerCase()) {
                case 'm':
                    e.preventDefault();
                    setAudioEnabled(!audioEnabled);
                    break;
                case 'v':
                    e.preventDefault();
                    setVideoEnabled(!videoEnabled);
                    break;
                case 'c':
                    e.preventDefault();
                    toggleChat();
                    break;
                case 'h':
                    e.preventDefault();
                    // Toggle raise hand
                    break;
                default:
                    break;
            }
        };

        const onContextMenu = (e) => {
            e.preventDefault();
            // Optionally log fraud_right_click_attempt
        };

        const onCopy = (e) => {
            e.preventDefault();
            // Optionally log fraud_copy_attempt
        };

        const onPaste = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                e.preventDefault();
                e.stopPropagation();
                // Optionally log fraud_paste_attempt
                alert('Pasting is disabled to ensure interview integrity.');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('contextmenu', onContextMenu, true);
        document.addEventListener('copy', onCopy, true);
        document.addEventListener('paste', onPaste, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('contextmenu', onContextMenu, true);
            document.removeEventListener('copy', onCopy, true);
            document.removeEventListener('paste', onPaste, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioEnabled, videoEnabled, showChat]);

    // console.log('recording state in Room:', isRecording);

    // Update timer every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Auto-hide controls after 3 seconds of no mouse movement
    useEffect(() => {
        let hideTimer;

        const handleMouseMove = () => {
            setControlsVisible(true);
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => {
                setControlsVisible(false);
            }, 3000);
        };

        document.addEventListener('mousemove', handleMouseMove);
        handleMouseMove(); // Show controls initially

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(hideTimer);
        };
    }, []);

    const handleSendMessage = () => {
        if (message.trim()) {
            sendChatMessage(message);
            setMessage('');
        }
    };

    // Find the screen sharing user (could be local or remote)
    const getScreenSharingUser = () => {
        if (!screenSharingUserId) return null;

        // Check if local user is screen sharing
        const isLocalSharing = screenSharingUserId === socketRef.current?.id;

        if (isLocalSharing) {
            if (!localScreenStream) return null;

            return {
                userId: socketRef.current.id, // Use socket ID consistently
                nickname,
                stream: localStream,
                screenStream: localScreenStream,
                audioEnabled,
                videoEnabled,
                isSpeaking: false,
                networkStrength: 'good'
            };
        }

        // Handle remote screen share
        return remoteUsers.find(user => user.userId === screenSharingUserId) || null;
    };


    const [stableScreenSharingUser, setStableScreenSharingUser] = useState(null);

    useEffect(() => {
        const user = getScreenSharingUser();
        // Only update when we have a valid user or need to clear
        if (user || !screenSharingUserId) {
            setStableScreenSharingUser(user || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenSharingUserId, localScreenStream, remoteUsers]);

    useEffect(() => {
        console.log('Stable screen share user:', stableScreenSharingUser);
        console.log('Raw state:', {
            screenSharingUserId,
            isScreenSharing,
            localScreenStream: !!localScreenStream,
            socketId: socketRef.current?.id
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableScreenSharingUser, screenSharingUserId, isScreenSharing, localScreenStream]);

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.style.cssText = `
            position: fixed;
            top: 80px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            z-index: 9999;
            backdrop-filter: blur(10px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        `;
        toast.textContent = message;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);
    };

    const onLeaveRoom = () => {
        leaveRoom();
        setJoined(false);
        setLocalStream(null);
    };

    const handleLeaveClick = () => {
        setShowLeaveConfirm(true);
    };

    const handleConfirmLeave = () => {
        setShowLeaveConfirm(false);
        onLeaveRoom();
    };

    const handleMuteUser = (userId) => {
        // Implement mute user functionality
        console.log('Mute user:', userId);
    };

    const handleRemoveUser = (userId) => {
        // Implement remove user functionality
        console.log('Remove user:', userId);
    };

    const handleChatPopout = () => {
        setShowChatPopout(!showChatPopout);
        if (!showChatPopout) {
            setShowChat(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const getCurrentSpeaker = () => {
        return remoteUsers.find(user => user.isSpeaking) || null;
    };

    // Calculate meeting duration
    const getMeetingDuration = () => {
        const duration = Math.floor((currentTime - meetingStartTime) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Get all participants including local user
    const getAllParticipants = () => {
        return [
            {
                userId: 'local',
                nickname,
                audioEnabled,
                videoEnabled,
                isLocal: true,
                isSpeaking: false,
                stream: localStream,
                networkStrength: 'good'
            },
            ...remoteUsers
        ];
    };

    // Determine video fit based on participant count and layout
    const getVideoFit = (participantCount) => {
        if (participantCount === 1) {
            return 'contain'; // Preserve aspect ratio for single participant
        }
        return 'cover'; // Use cover for multi-participant grids
    };

    const handleViewModeChange = (newMode) => {
        setViewMode(newMode);
        setShowViewOptions(false);
    };

    const allParticipants = getAllParticipants();
    const videoFit = getVideoFit(participantCount);

    return (
        <div className={`meet-room-container ${participantCount === 1 ? 'single-participant' : ''}`}>
            {/* Meeting Info Bar */}
            <div className="meeting-info-bar">
                <div className="meeting-details">
                    <span className="meeting-id">Meeting ID: {roomId}</span>
                    <span className="meeting-timer">{getMeetingDuration()}</span>
                    <span className="participant-count">{participantCount} participants</span>
                </div>
                <div className="meeting-actions">
                    <div className="view-mode-selector">
                        <button
                            className="view-mode-btn"
                            onClick={() => setShowViewOptions(!showViewOptions)}
                            title="View Options"
                        >
                            <span className="view-icon">⊞</span>
                            <span className="view-label">{viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}</span>
                        </button>

                        {showViewOptions && (
                            <div className="view-options-menu">
                                <button
                                    className={`view-option ${viewMode === 'gallery' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('gallery')}
                                >
                                    <span className="option-icon">⊞</span>
                                    Gallery
                                </button>
                                <button
                                    className={`view-option ${viewMode === 'speaker' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('speaker')}
                                >
                                    <span className="option-icon">👤</span>
                                    Speaker
                                </button>
                                <button
                                    className={`view-option ${viewMode === 'focus' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('focus')}
                                >
                                    <span className="option-icon">📌</span>
                                    Focus
                                </button>
                                <button
                                    className={`view-option ${viewMode === 'immersive' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('immersive')}
                                >
                                    <span className="option-icon">🌐</span>
                                    Immersive
                                </button>
                            </div>
                        )}
                    </div>
                    <button className="info-btn" title="Meeting Info">
                        ℹ️
                    </button>
                    <button className="info-btn" title="Recording"
                    //  onClick={isRecording ? stopRecording : startRecording}
                     >
                        🔴
                    </button>
                    {isRecording && (
                        <div className="recording-indicator">
                            <span className="dot" /> Recording
                        </div>
                    )}

                </div>
            </div>
            {recordingMessage && (
                <div className="recording-toast">
                    {recordingMessage}
                </div>
            )}


            <div className="room-content">
                {/* Main content area that adjusts based on chat state */}
                <div className={`main-content-area ${showChat ? 'with-chat' : ''}`}>
                    {/* Screen share layout: main area + sidebar thumbnails */}
                    {isScreenShareActive ? (
                        <div className="screen-share-layout">
                            <div className="main-share-area">
                                <VideoPlayer
                                    stream={null}
                                    screenStream={sharedStream}
                                    userId={screenSharingUserId || 'share'}
                                    nickname={'Screen Share'}
                                    isLocal={screenSharingUserId === socketRef.current?.id}
                                    isScreenShare={true}
                                    isPinned={false}
                                    isSpeaking={false}
                                    audioEnabled={true}
                                    videoEnabled={true}
                                    fit={'contain'}
                                />
                            </div>
                            <div className="thumbnail-strip">
                                {thumbnailParticipants.map(p => (
                                    <VideoPlayer
                                        key={p.id}
                                        stream={p.stream}
                                        userId={p.id}
                                        nickname={p.id === socketRef.current?.id ? `${p.nickname} (You)` : (p.nickname || p.id)}
                                        isLocal={p.id === socketRef.current?.id}
                                        isSmall={true}
                                        isPinned={false}
                                        isSpeaking={false}
                                        audioEnabled={true}
                                        videoEnabled={true}
                                        fit={'cover'}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="main-video-area">
                            <div
                                className="video-grid"
                                style={{
                                    gridTemplateColumns: gridConfig.isAutoFit
                                        ? `repeat(auto-fit, minmax(${gridConfig.tileWidth}px, 1fr))`
                                        : `repeat(${gridConfig.columns}, 1fr)`,
                                    gridTemplateRows: gridConfig.isAutoFit ? 'auto' : `repeat(${Math.ceil(participantCount / Number(gridConfig.columns))}, 1fr)`,
                                    gap: '8px',
                                    padding: '8px',
                                    height: `${containerSize.height}px`,
                                    width: '100%'
                                }}
                            >
                                {allParticipants.map(user => (
                                    <VideoPlayer
                                        key={user.userId}
                                        stream={user.stream}
                                        userId={user.userId}
                                        nickname={user.nickname}
                                        isLocal={user.isLocal}
                                        isPinned={pinnedUserId === user.userId}
                                        isSpeaking={user.isSpeaking}
                                        audioEnabled={user.audioEnabled}
                                        videoEnabled={user.videoEnabled}
                                        networkStrength={user.networkStrength}
                                        onPinUser={handlePinUser}
                                        fit={videoFit}
                                        className={`${participantCount === 1 ? 'single' : ''}`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Chat drawer */}
                {showChat && (
                    <div className="chat-drawer">
                        <Chat
                            messages={chatMessages}
                            message={message}
                            setMessage={setMessage}
                            onSendMessage={handleSendMessage}
                            onClose={() => setShowChat(false)}
                            onPopout={handleChatPopout}
                        />
                    </div>
                )}
            </div>

            {/* Chat Popout */}
            {showChatPopout && (
                <ChatPopout
                    messages={chatMessages}
                    message={message}
                    setMessage={setMessage}
                    onSendMessage={handleSendMessage}
                    nickname={nickname}
                    socketRef={socketRef}
                    roomId={roomId}
                />
            )}

            {/* Settings Modal */}
            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                localStream={localStream}
                onUpdateSettings={(settings) => {
                    if (settings.theme) {
                        document.documentElement.setAttribute('data-theme', settings.theme);
                    }
                }}
            />

            {/* Leave Confirmation Modal */}
            <LeaveConfirmationModal
                isOpen={showLeaveConfirm}
                onConfirm={handleConfirmLeave}
                onCancel={() => setShowLeaveConfirm(false)}
            />

            {/* Bottom control bar */}
            <div className={`bottom-controls ${controlsVisible ? 'visible' : 'hidden'}`}>
                <Controls
                    audioEnabled={audioEnabled}
                    videoEnabled={videoEnabled}
                    isScreenSharing={stableScreenSharingUser?.userId && stableScreenSharingUser?.userId === socketRef.current?.id}
                    participantCount={participantCount}
                    chatVisible={showChat}
                    localStream={localStream}
                    screenStream={stableScreenSharingUser?.screenStream}
                    socketRef={socketRef}
                    roomId={roomId}
                    nickname={nickname}
                    onToggleAudio={() => {
                        const newState = !audioEnabled;
                        setAudioEnabled(newState);
                        if (localStream) {
                            localStream.getAudioTracks()[0].enabled = newState;
                        }
                    }}
                    onToggleVideo={() => {
                        handleCameraToggle();
                    }}
                    onScreenShare={handleScreenShare}
                    onShowParticipants={toggleParticipants}
                    onShowChat={toggleChat}
                    onLeave={handleLeaveClick}
                    onToggleSettings={() => setShowSettings(true)}
                />
            </div>

            {/* Participants Panel */}
            {showParticipants && (
                <div className="participants-drawer">
                    <Participants
                        remoteUsers={remoteUsers}
                        localUser={{
                            nickname,
                            audioEnabled,
                            videoEnabled,
                            isLocal: true
                        }}
                        raisedHands={raisedHands}
                        isHost={true}
                        onMuteUser={handleMuteUser}
                        onRemoveUser={handleRemoveUser}
                    />
                </div>
            )}
        </div>
    );
};

export default Room;
